import { useEffect, useRef, useCallback } from 'react'

import {
  SATU_EVENT_TRACKER_EVENT_NAME,
  SATU_EVENT_DEFAULT_EVENT_TYPE,
} from 'utils/satu-event-tracker/config/event-tracking'
import { useSatuEventTracker } from './useSatuEventTracker'

interface ScrollEventAttributes {
  screen_name?: string
  scroll_depth?: string
  scroll_direction?: string
  [key: string]: any
}

interface UseScrollTrackerOptions {
  eventName?: string
  activityType: string
  attributes?: ScrollEventAttributes
  trackOnce?: boolean
  scrollThreshold?: number
  debounceMs?: number
  scrollElement?: React.RefObject<HTMLElement> | null
  enabled?: boolean
}

/**
 * Custom hook to track scroll/swipe events with SatuEvent tracker.
 * Automatically sets up scroll listeners and tracks user scroll interactions.
 *
 * @example
 * ```tsx
 * // Track only the first scroll
 * useScrollTracker({
 *   trackOnce: true,
 *   attributes: {
 *     screen_name: 'ide_praktik_video_inspirasi',
 *   },
 * })
 *
 * // Track every scroll (with debounce)
 * useScrollTracker({
 *   trackOnce: false,
 *   debounceMs: 1000, // Track at most once per second
 *   attributes: {
 *     screen_name: 'playlist_detail',
 *   },
 * })
 *
 * // Track scroll on a specific element
 * const containerRef = useRef<HTMLDivElement>(null)
 * useScrollTracker({
 *   scrollElement: containerRef,
 *   attributes: {
 *     screen_name: 'custom_container',
 *   },
 * })
 * ```
 */
export const useScrollTracker = ({
  eventName = SATU_EVENT_DEFAULT_EVENT_TYPE,
  activityType,
  attributes = {},
  trackOnce = true,
  scrollThreshold = 50,
  debounceMs = 500,
  scrollElement = null,
  enabled = true,
}: UseScrollTrackerOptions) => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const hasTracked = useRef(false)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const lastScrollY = useRef(0)

  const trackScroll = useCallback(() => {
    if (!enabled) return
    if (trackOnce && hasTracked.current) return

    const element = scrollElement?.current || window
    const isWindow = element === window

    const currentScrollY = isWindow
      ? window.scrollY || window.pageYOffset
      : (element as HTMLElement).scrollTop

    const scrollDifference = Math.abs(currentScrollY - lastScrollY.current)

    // Only track if scrolled past threshold
    if (scrollDifference < scrollThreshold) return

    const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up'

    const scrollHeight = isWindow
      ? document.documentElement.scrollHeight
      : (element as HTMLElement).scrollHeight

    const clientHeight = isWindow
      ? window.innerHeight
      : (element as HTMLElement).clientHeight

    const scrollDepth = Math.round(
      (currentScrollY / (scrollHeight - clientHeight)) * 100
    )

    SETrackEvent({
      eventName: SATU_EVENT_TRACKER_EVENT_NAME,
      eventParams: {
        activity_type: activityType,
        event_name: eventName,
        attributes: SETransformAttrs({
          ...attributes,
          scroll_direction: scrollDirection,
          scroll_precentage: `${Math.min(scrollDepth, 100)}%`,
        }),
      },
    })

    lastScrollY.current = currentScrollY
    hasTracked.current = true
  }, [
    SETrackEvent,
    SETransformAttrs,
    eventName,
    activityType,
    attributes,
    trackOnce,
    scrollThreshold,
    scrollElement,
    enabled,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const element = scrollElement?.current || window

    const handleScroll = () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      debounceTimer.current = setTimeout(() => {
        trackScroll()
      }, debounceMs)
    }

    element.addEventListener('scroll', handleScroll, { passive: true })

    // Only add touchmove for window
    if (element === window) {
      window.addEventListener('touchmove', handleScroll, { passive: true })
    }

    return () => {
      element.removeEventListener('scroll', handleScroll)
      if (element === window) {
        window.removeEventListener('touchmove', handleScroll)
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [trackScroll, debounceMs, scrollElement])

  // Return a manual trigger function in case needed
  return trackScroll
}
